/**
 * Exercise logging form for strength/crossfit activities.
 * Allows users to log sets/reps/weight for exercises performed in an activity.
 */

import { useState, useCallback } from "react";
import { useLogExercises } from "@/hooks/useExercises";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import type { ExerciseLogCreate } from "@/lib/exerciseApi";

interface ExerciseLogFormProps {
  activityId: string;
  onSaved?: () => void;
}

interface SetDraft {
  id: number;
  exercise_name: string;
  set_number: number;
  reps: string;
  weight_kg: string;
  rpe: string;
}

let nextId = 1;
function makeEmptySet(setNumber = 1): SetDraft {
  return {
    id: nextId++,
    exercise_name: "",
    set_number: setNumber,
    reps: "",
    weight_kg: "",
    rpe: "",
  };
}

export function ExerciseLogForm({ activityId, onSaved }: ExerciseLogFormProps) {
  const [sets, setSets] = useState<SetDraft[]>([makeEmptySet()]);
  const logMutation = useLogExercises();

  const addSet = useCallback(() => {
    setSets((prev) => {
      const lastSet = prev[prev.length - 1];
      return [
        ...prev,
        makeEmptySet(lastSet ? lastSet.set_number + 1 : 1),
      ];
    });
  }, []);

  const removeSet = useCallback((id: number) => {
    setSets((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const updateSet = useCallback(
    (id: number, field: keyof SetDraft, value: string) => {
      setSets((prev) =>
        prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
      );
    },
    [],
  );

  const handleSave = useCallback(() => {
    const validSets: ExerciseLogCreate[] = sets
      .filter((s) => s.exercise_name.trim())
      .map((s) => ({
        exercise_name: s.exercise_name.trim(),
        set_number: s.set_number,
        reps: s.reps ? parseInt(s.reps, 10) : null,
        weight_kg: s.weight_kg ? parseFloat(s.weight_kg) : null,
        rpe: s.rpe ? parseFloat(s.rpe) : null,
      }));

    if (!validSets.length) {
      toast({ title: "No exercises", description: "Add at least one exercise name." });
      return;
    }

    logMutation.mutate(
      { activity_id: activityId, sets: validSets },
      {
        onSuccess: () => {
          toast({ title: "Saved", description: `${validSets.length} set(s) logged.` });
          onSaved?.();
        },
        onError: () => {
          toast({ title: "Error", description: "Failed to save exercises.", variant: "destructive" });
        },
      },
    );
  }, [sets, activityId, logMutation, onSaved]);

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {sets.map((set) => (
          <div key={set.id} className="grid grid-cols-[1fr_60px_60px_60px_32px] gap-1.5 items-center">
            <input
              type="text"
              placeholder="Exercise name"
              value={set.exercise_name}
              onChange={(e) => updateSet(set.id, "exercise_name", e.target.value)}
              className="h-8 rounded border border-white/[0.08] bg-white/[0.03] px-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[rgba(0,255,200,0.3)]"
            />
            <input
              type="number"
              placeholder="Reps"
              value={set.reps}
              onChange={(e) => updateSet(set.id, "reps", e.target.value)}
              className="h-8 rounded border border-white/[0.08] bg-white/[0.03] px-2 text-xs text-foreground tabular-nums text-center placeholder:text-muted-foreground focus:outline-none focus:border-[rgba(0,255,200,0.3)]"
            />
            <input
              type="number"
              placeholder="kg"
              value={set.weight_kg}
              onChange={(e) => updateSet(set.id, "weight_kg", e.target.value)}
              className="h-8 rounded border border-white/[0.08] bg-white/[0.03] px-2 text-xs text-foreground tabular-nums text-center placeholder:text-muted-foreground focus:outline-none focus:border-[rgba(0,255,200,0.3)]"
            />
            <input
              type="number"
              placeholder="RPE"
              value={set.rpe}
              onChange={(e) => updateSet(set.id, "rpe", e.target.value)}
              className="h-8 rounded border border-white/[0.08] bg-white/[0.03] px-2 text-xs text-foreground tabular-nums text-center placeholder:text-muted-foreground focus:outline-none focus:border-[rgba(0,255,200,0.3)]"
              min="1"
              max="10"
              step="0.5"
            />
            <button
              type="button"
              onClick={() => removeSet(set.id)}
              className="h-8 w-8 flex items-center justify-center rounded border border-white/[0.06] text-muted-foreground hover:text-red-400 hover:border-red-400/30 transition-colors"
              aria-label="Remove set"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={addSet} className="text-xs gap-1">
          <Plus className="h-3.5 w-3.5" />
          Add Set
        </Button>
        <div className="flex-1" />
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={logMutation.isPending}
          className="text-xs gap-1"
        >
          {logMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save
        </Button>
      </div>
    </div>
  );
}
